import React, { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Space,
  message,
  Typography,
  Row,
  Col,
  Tag,
  Tabs,
  Spin,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import {
  taskService,
  Task,
  TaskSubTask,
  TaskDraftReport,
  TaskComment,
  TaskReview,
  TaskTimelog,
} from '../services/tasks';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

export const TaskDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [task, setTask] = useState<Task | null>(null);
  const [taskSubTasks, setTaskSubTasks] = useState<TaskSubTask[]>([]);
  const [draftReports, setDraftReports] = useState<TaskDraftReport[]>([]);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [reviews, setReviews] = useState<TaskReview[]>([]);
  const [timelogs, setTimelogs] = useState<TaskTimelog[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      loadTask(parseInt(id, 10));
    }
  }, [id]);

  const loadTask = async (taskId: number) => {
    setLoading(true);
    try {
      const loadedTask = await taskService.getById(taskId);
      setTask(loadedTask);
      
      // Handle both camelCase and snake_case from backend
      const rawSubTasks = (loadedTask as any).subTasks || (loadedTask as any).sub_tasks || [];
      const subTasks = rawSubTasks.map((st: any) => ({
        id: st.id,
        task_id: st.task_id,
        sub_task_template_id: st.sub_task_template_id,
        name: st.name,
        completed: st.completed,
        created_by: st.created_by,
        updated_by: st.updated_by,
        created_at: st.created_at,
        updated_at: st.updated_at,
        subTaskTemplate: st.subTaskTemplate || st.sub_task_template || undefined,
        creator: st.creator || undefined,
        updater: st.updater || undefined,
      }));
      setTaskSubTasks(subTasks);
      
      const draftReports = (loadedTask as any).draftReports || (loadedTask as any).draft_reports || [];
      setDraftReports(draftReports);
      const comments = (loadedTask as any).comments || [];
      setComments(comments);
      const reviews = (loadedTask as any).reviews || [];
      setReviews(reviews);
      const timelogs = (loadedTask as any).timelogs || [];
      setTimelogs(timelogs);
    } catch {
      message.error('Failed to load task');
      navigate('/tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!id) {
      message.error('Task ID is required');
      return;
    }
    setSubmitting(true);
    try {
      const referencePayload: Record<string, any> = {};
      if (task) {
        if (task.type !== undefined) referencePayload.type = task.type;
        if (task.reference_id !== undefined) referencePayload.reference_id = task.reference_id;
        if (task.customer_id !== undefined) referencePayload.customer_id = task.customer_id;
        if (task.business_id !== undefined) referencePayload.business_id = task.business_id;
      }
      await taskService.update(parseInt(id, 10), {
        status: 'Completed',
        ...referencePayload,
      });
      message.success('Task status updated to Completed');
      await loadTask(parseInt(id, 10));
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update task status');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTaskNumber = (taskId: number) => {
    return `#${taskId.toString().padStart(7, '0')}`;
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    return `${day}/${month}/${year} ${displayHours}:${minutes}${ampm}`;
  };

  const calculateTotalHours = () => {
    return timelogs.reduce((total, timelog) => total + (timelog.hour || 0), 0);
  };

  const handleViewDraftReport = async (reportId: number) => {
    try {
      const response = await api.get(`/task-draft-reports/${reportId}/view`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error: any) {
      message.error('Failed to view file');
    }
  };

  const handleDownloadDraftReport = async (reportId: number, fileName: string) => {
    try {
      const response = await api.get(`/task-draft-reports/${reportId}/download`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'draft-report';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error: any) {
      message.error('Failed to download file');
    }
  };

  if (loading) {
    return (
      <Card>
        <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '40px' }} />
      </Card>
    );
  }

  if (!task) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Text>Task not found</Text>
        </div>
      </Card>
    );
  }

  const getTaskDisplayName = () => {
    if (id) {
      return `${formatTaskNumber(parseInt(id, 10))} - ${task.customer?.name || 'Customer'} - ${task.taskType?.name || 'Task'}`;
    }
    return 'Task Detail';
  };

  return (
    <Card>
      <div style={{ marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/tasks')}
          style={{ marginBottom: 16 }}
        >
          Back
        </Button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {getTaskDisplayName()}
            </Title>
            <div style={{ color: '#999', marginTop: 8 }}>
              Tasks / {formatTaskNumber(parseInt(id!, 10))}
            </div>
          </div>
          <Space>
            <Button danger icon={<DeleteOutlined />} onClick={() => {
              if (window.confirm('Are you sure you want to delete this task?')) {
                taskService.delete(parseInt(id!, 10)).then(() => {
                  message.success('Task deleted');
                  navigate('/tasks');
                }).catch(() => message.error('Failed to delete task'));
              }
            }}>
              Delete
            </Button>
            <Button type="primary" icon={<EditOutlined />} onClick={() => navigate(`/tasks/${id}/edit`)}>
              Edit
            </Button>
            {task.status === 'Approve' && (
              <Button 
                onClick={handleComplete}
                loading={submitting}
                style={{
                  backgroundColor: '#fff',
                  borderColor: '#d9d9d9',
                  color: '#52c41a',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  height: '32px',
                  padding: '4px 16px',
                }}
                icon={
                  <div style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: '#52c41a',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <CheckOutlined style={{ color: '#fff', fontSize: '14px' }} />
                  </div>
                }
              >
                <span style={{ color: '#52c41a', fontWeight: 500 }}>Completed</span>
              </Button>
            )}
            <Tag color={
              task.status === 'Plan' ? 'blue' :
              task.status === 'Progress' ? 'orange' :
              task.status === 'Review' ? 'purple' :
              task.status === 'Approve' ? 'cyan' :
              task.status === 'Completed' ? 'green' : 'default'
            }>
              {task.status || 'Plan'}
            </Tag>
          </Space>
        </div>
      </div>

      <Row gutter={24}>
        {/* Left Column */}
        <Col span={8}>
          {/* Basic Information */}
          <Card title="Basic Information" style={{ marginBottom: 16 }} bodyStyle={{ padding: '16px' }}>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>Customer</Text>
              <div style={{ marginTop: 4 }}>
                <Text strong>{task.customer?.name || task.customer_id || '-'}</Text>
              </div>
            </div>

            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Type</Text>
                  <div style={{ marginTop: 4 }}>
                    <Text strong>{task.taskType?.name || task.task_type_id || '-'}</Text>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Year</Text>
                  <div style={{ marginTop: 4 }}>
                    <Text strong>{task.year || '-'}</Text>
                  </div>
                </div>
              </Col>
            </Row>

            <div>
              <Text type="secondary" style={{ fontSize: '12px' }}>Task</Text>
              <div style={{ marginTop: 4 }}>
                <Text strong>{task.taskTemplate?.name || task.task_template_id || '-'}</Text>
              </div>
            </div>
          </Card>

          {/* Date & Hours */}
          <Card title="Date & Hours" style={{ marginBottom: 16 }} bodyStyle={{ padding: '16px' }}>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Create</Text>
                  <div style={{ marginTop: 4 }}>
                    <Text strong>{task.created_date ? dayjs(task.created_date).format('DD/MM/YYYY') : '-'}</Text>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Due</Text>
                  <div style={{ marginTop: 4 }}>
                    <Text strong>{task.due_date ? dayjs(task.due_date).format('DD/MM/YYYY') : '-'}</Text>
                  </div>
                </div>
              </Col>
            </Row>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>Deadline</Text>
              <div style={{ marginTop: 4 }}>
                <Text strong>{task.deadline ? dayjs(task.deadline).format('DD/MM/YYYY') : '-'}</Text>
              </div>
            </div>
            <Row gutter={16}>
              <Col span={12}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Plan</Text>
                  <div style={{ marginTop: 4 }}>
                    <Text strong>{task.plan_hour || '-'}</Text>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Actual</Text>
                  <div style={{ marginTop: 4 }}>
                    <Text strong>{task.actual_hour ?? calculateTotalHours()}</Text>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Billing */}
          <Card title="Billing" style={{ marginBottom: 16 }} bodyStyle={{ padding: '16px' }}>
            <Row gutter={16}>
              <Col span={12}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Quotation #</Text>
                  <div style={{ marginTop: 4 }}>
                    <Text strong>{task.quotation?.quotation_number || '-'}</Text>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Invoice #</Text>
                  <div style={{ marginTop: 4 }}>
                    <Text strong>{task.quotation?.invoice?.invoice_number || '-'}</Text>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Agent */}
          <Card title="Agent" style={{ marginBottom: 16 }} bodyStyle={{ padding: '16px' }}>
            <Row gutter={16}>
              <Col span={12}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Team</Text>
                  <div style={{ marginTop: 4 }}>
                    <Text strong>{task.customer?.team?.name || '-'}</Text>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Staff</Text>
                  <div style={{ marginTop: 4 }}>
                    <Text strong>{task.quotation?.staff?.name || '-'}</Text>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Middle Column */}
        <Col span={8}>
          {/* Sub-Tasks */}
          <Card
            title="Sub-Tasks"
            style={{ marginBottom: 24, border: '2px solid #1890ff' }}
            headStyle={{ backgroundColor: '#e6f7ff' }}
          >
            {taskSubTasks.map((subTask: TaskSubTask, index: number) => (
              <div key={subTask.id || index} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Text delete={subTask.completed} style={{ flex: 1 }}>
                    {subTask.name || subTask.subTaskTemplate?.name || '-'}
                  </Text>
                  {subTask.completed && <Tag color="green">Completed</Tag>}
                </div>
                <div style={{ marginLeft: 24, fontSize: '12px', color: '#999' }}>
                  By {subTask.completed 
                    ? (subTask.updater?.name || subTask.creator?.name || 'Unknown')
                    : (subTask.creator?.name || 'Unknown')
                  } {subTask.completed && subTask.updated_at 
                    ? formatDateTime(subTask.updated_at)
                    : subTask.created_at 
                    ? formatDateTime(subTask.created_at)
                    : ''
                  }
                </div>
              </div>
            ))}
            {taskSubTasks.length === 0 && (
              <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                No sub tasks
              </div>
            )}
          </Card>

          {/* Draft Reports */}
          <Card
            title="Draft Reports"
            style={{ marginBottom: 24, border: '2px solid #ff9800' }}
          >
            {draftReports.map((report: TaskDraftReport) => {
              const fileName = report.attachment ? report.attachment.split('/').pop() : 'No attachment';
              return (
                <div key={report.id} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, gap: 8 }}>
                    {report.attachment ? (
                      <>
                        <Button
                          type="link"
                          onClick={() => handleViewDraftReport(report.id!)}
                          size="small"
                          style={{ padding: 0, height: 'auto', color: '#1890ff', flex: 1, minWidth: 0, textAlign: 'left', justifyContent: 'flex-start' }}
                        >
                          <span style={{ 
                            maxWidth: '100%', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            display: 'inline-block',
                            textAlign: 'left'
                          }}>
                            {fileName}
                          </span>
                        </Button>
                        <Space style={{ flexShrink: 0 }}>
                          <Button
                            type="link"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownloadDraftReport(report.id!, fileName || 'draft-report')}
                            size="small"
                            style={{ padding: 0 }}
                          />
                        </Space>
                      </>
                    ) : (
                      <span>No attachment</span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    By {report.creator?.name || 'Unknown'} {formatDateTime(report.created_at)}
                  </div>
                </div>
              );
            })}
            {draftReports.length === 0 && (
              <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                No draft reports
              </div>
            )}
          </Card>
        </Col>

        {/* Right Column */}
        <Col span={8}>
          {/* Comment / Review */}
          <Card title="Comment / Review" style={{ marginBottom: 24, border: '2px solid #d9d9d9' }}>
            <Tabs
              defaultActiveKey="comment"
              items={[
                {
                  key: 'comment',
                  label: 'Comment',
                  children: (
                    <div style={{ marginBottom: 16 }}>
                      {comments.map((comment: TaskComment, index: number) => (
                        <div
                          key={comment.id || index}
                          style={{
                            marginBottom: 8,
                            padding: '8px 12px',
                            background: '#fafafa',
                            borderRadius: 4,
                          }}
                        >
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {new Date(comment.created_at).toLocaleDateString()} {comment.comment || '-'} by {comment.user?.name || comment.creator?.name || '-'}
                          </div>
                        </div>
                      ))}
                      {comments.length === 0 && (
                        <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                          No comments yet.
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'review',
                  label: 'Review',
                  children: (
                    <div style={{ marginBottom: 16 }}>
                      {reviews.map((review: TaskReview, index: number) => (
                        <div
                          key={review.id || index}
                          style={{
                            marginBottom: 8,
                            padding: '8px 12px',
                            background: '#fafafa',
                            borderRadius: 4,
                          }}
                        >
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {new Date(review.created_at).toLocaleDateString()} {review.review || '-'} by {review.creator?.name || '-'}
                          </div>
                        </div>
                      ))}
                      {reviews.length === 0 && (
                        <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                          No reviews yet.
                        </div>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </Card>

          {/* Actual Timelog */}
          <Card 
            title="Actual" 
            style={{ marginBottom: 24, border: '2px solid #faad14' }}
            headStyle={{ backgroundColor: '#fffbe6' }}
          >
            <div style={{ marginBottom: 16 }}>
              {timelogs.length === 0 ? (
                <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                  No timelogs yet
                </div>
              ) : (
                timelogs.map((timelog: TaskTimelog) => (
                  <div 
                    key={timelog.id} 
                    style={{ 
                      marginBottom: 12, 
                      padding: '12px',
                      background: '#fafafa',
                      borderRadius: 4,
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                    }}
                  >
                    <Space>
                      <ClockCircleOutlined />
                      <span>{timelog.hour || 0}h</span>
                      <span>{new Date(timelog.date).toLocaleDateString()}</span>
                      <span>{timelog.description || 'Description'}</span>
                    </Space>
                  </div>
                ))
              )}
            </div>
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0', textAlign: 'right' }}>
              <Text strong>Total: {calculateTotalHours()} hours</Text>
            </div>
          </Card>
        </Col>
      </Row>
    </Card>
  );
};
